import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import type { SelectOption } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { roomCategoryService } from 'api/services/roomCategoryService';
import { propertyService } from 'api/services/propertyService';
import type { RoomCategory, CreateRoomCategoryRequest, UpdateRoomCategoryRequest } from 'api/types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface RoomCategoryModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: RoomCategory | null;
  onSave: () => void;
  onClose: () => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onReopen?: () => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, categoryCode?: string) => void;
  initialPropertyOption?: SelectOption | null;
}

interface RoomCategoryFormData {
  propertyId: string;
  categoryCode: string;
  categoryName: string;
  shortDescription: string;
  longDescription: string;
  baseOccupancy: string;
  maxOccupancy: string;
  maxAdults: string;
  maxChildren: string;
  bedType: string;
  bedCount: string;
  roomSizeSqft: string;
  roomView: string;
  smokingAllowed: boolean;
  totalInventory: string;
  displayOrder: string;
  isActive: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_FORM_DATA: RoomCategoryFormData = {
  propertyId: '',
  categoryCode: '',
  categoryName: '',
  shortDescription: '',
  longDescription: '',
  baseOccupancy: '2',
  maxOccupancy: '3',
  maxAdults: '2',
  maxChildren: '1',
  bedType: 'King',
  bedCount: '1',
  roomSizeSqft: '',
  roomView: '',
  smokingAllowed: false,
  totalInventory: '',
  displayOrder: '1',
  isActive: true,
};

const bedTypeOptions = [
  { value: 'King', label: 'King' },
  { value: 'Queen', label: 'Queen' },
  { value: 'Twin', label: 'Twin' },
  { value: 'Double', label: 'Double' },
  { value: 'Single', label: 'Single' },
  { value: 'Bunk', label: 'Bunk' },
];

const roomViewOptions = [
  { value: 'Sea', label: 'Sea View' },
  { value: 'Garden', label: 'Garden View' },
  { value: 'Pool', label: 'Pool View' },
  { value: 'City', label: 'City View' },
  { value: 'Mountain', label: 'Mountain View' },
  { value: 'No View', label: 'No View' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const RoomCategoryModal: React.FC<RoomCategoryModalProps> = ({
  isOpen, mode, record, onSave, onClose,
  tabId, moduleName, onReopen, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
  initialPropertyOption,
}) => {
  const { minimize } = useModalMinimize();

  // --- Property async search (SyutSelect caches empty-input results internally for label resolution) ---
  const loadPropertyOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 500, includeInactive: false });
    return (res.data || []).map(p => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<RoomCategoryFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // --- Concurrency conflict state ---
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const effectiveRecordRef = useRef<RoomCategory | null | undefined>(record);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    let data: RoomCategoryFormData;
    if ((mode === 'edit' || mode === 'copy') && rec) {
      data = {
        propertyId: rec.propertyId || '',
        categoryCode: mode === 'copy' ? '' : (rec.categoryCode || ''),
        categoryName: rec.categoryName || '',
        shortDescription: rec.shortDescription || '',
        longDescription: rec.longDescription || '',
        baseOccupancy: rec.baseOccupancy != null ? String(rec.baseOccupancy) : '2',
        maxOccupancy: rec.maxOccupancy != null ? String(rec.maxOccupancy) : '3',
        maxAdults: rec.maxAdults != null ? String(rec.maxAdults) : '2',
        maxChildren: rec.maxChildren != null ? String(rec.maxChildren) : '1',
        bedType: rec.bedType || 'King',
        bedCount: rec.bedCount != null ? String(rec.bedCount) : '1',
        roomSizeSqft: rec.roomSizeSqft != null ? String(rec.roomSizeSqft) : '',
        roomView: rec.roomView || '',
        smokingAllowed: rec.smokingAllowed,
        totalInventory: rec.totalInventory != null ? String(rec.totalInventory) : '',
        displayOrder: rec.displayOrder != null ? String(rec.displayOrder) : '1',
        isActive: rec.isActive,
      };
    } else {
      data = { ...INITIAL_FORM_DATA };
    }
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Unsaved changes tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler (checks dirty state) ---
  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordRef.current?.id || 'new'}`;
    }
    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.categoryCode?.trim();
      const name = currentFormData.categoryName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add Room Category (${idx})`;
    } else if (currentMode === 'copy') {
      const code = currentFormData.categoryCode?.trim();
      const name = currentFormData.categoryName?.trim();
      const idx = currentCopyInstanceId || '1';
      title = code ? `Copy - ${code} (${idx})` : name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.categoryCode || 'Room Category'}`;
    }
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? currentFormData.categoryCode : undefined,
        );
        else if (onReopen) onReopen();
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, onReopen]);

  // Expose handleMinimize to parent for auto-minimize on new copy
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate + show save confirmation ---
  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};

    if (!formData.propertyId) {
      errors.propertyId = 'Property is required';
    }

    if (!formData.categoryCode.trim()) {
      errors.categoryCode = 'Category Code is required';
    } else if (formData.categoryCode.length > 20) {
      errors.categoryCode = 'Category Code cannot exceed 20 characters';
    }

    if (!formData.categoryName.trim()) {
      errors.categoryName = 'Category Name is required';
    } else if (formData.categoryName.length > 100) {
      errors.categoryName = 'Category Name cannot exceed 100 characters';
    }

    if (formData.shortDescription && formData.shortDescription.length > 200) {
      errors.shortDescription = 'Short Description cannot exceed 200 characters';
    }

    if (formData.longDescription && formData.longDescription.length > 1000) {
      errors.longDescription = 'Long Description cannot exceed 1000 characters';
    }

    const baseOcc = parseInt(formData.baseOccupancy, 10);
    const maxOcc = parseInt(formData.maxOccupancy, 10);
    if (isNaN(baseOcc) || baseOcc < 1) {
      errors.baseOccupancy = 'Base Occupancy must be at least 1';
    }
    if (isNaN(maxOcc) || maxOcc < 1) {
      errors.maxOccupancy = 'Max Occupancy must be at least 1';
    }
    if (!isNaN(baseOcc) && !isNaN(maxOcc) && maxOcc < baseOcc) {
      errors.maxOccupancy = 'Max Occupancy must be >= Base Occupancy';
    }

    const maxAdults = parseInt(formData.maxAdults, 10);
    if (isNaN(maxAdults) || maxAdults < 0) {
      errors.maxAdults = 'Max Adults must be 0 or greater';
    } else if (!isNaN(maxOcc) && maxAdults > maxOcc) {
      errors.maxAdults = 'Max Adults cannot exceed Max Occupancy';
    }

    const maxChildren = parseInt(formData.maxChildren, 10);
    if (isNaN(maxChildren) || maxChildren < 0) {
      errors.maxChildren = 'Max Children must be 0 or greater';
    } else if (!isNaN(maxOcc) && maxChildren > maxOcc) {
      errors.maxChildren = 'Max Children cannot exceed Max Occupancy';
    }

    // Combined cap: at booking time Adults + Children must fit Max Occupancy.
    // Allowing the master to define MaxAdults + MaxChildren > MaxOccupancy is
    // confusing — operators read those numbers as "how many of each you can put
    // in the room", and the booking screen then silently caps below that.
    if (!isNaN(maxAdults) && !isNaN(maxChildren) && !isNaN(maxOcc)
        && maxAdults + maxChildren > maxOcc) {
      errors.maxOccupancy = `Max Adults (${maxAdults}) + Max Children (${maxChildren}) cannot exceed Max Occupancy (${maxOcc})`;
    }

    if (!formData.bedType) {
      errors.bedType = 'Bed Type is required';
    }

    const bedCount = parseInt(formData.bedCount, 10);
    if (isNaN(bedCount) || bedCount < 1) {
      errors.bedCount = 'Bed Count must be at least 1';
    }

    if (!formData.totalInventory || parseInt(formData.totalInventory, 10) < 0) {
      errors.totalInventory = 'Total Inventory is required';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    // Async code uniqueness validation for create/copy modes
    if (mode !== 'edit') {
      try {
        const response = await roomCategoryService.validateCode(formData.categoryCode.trim(), formData.propertyId);
        if (!response.data) {
          setFieldErrors({ categoryCode: 'This category code already exists' });
          requestAnimationFrame(() => {
            const el = document.querySelector<HTMLElement>('[data-field="categoryCode"]');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.querySelector<HTMLElement>('input')?.focus();
            }
          });
          return;
        }
      } catch {
        // If validation endpoint fails, proceed with save
      }
    }

    // Async name uniqueness validation
    {
      try {
        const excludeId = mode === 'edit' && record ? record.id : undefined;
        const response = await roomCategoryService.validateName(formData.categoryName.trim(), formData.propertyId, excludeId);
        if (!response.data) {
          setFieldErrors({ categoryName: 'This category name already exists' });
          requestAnimationFrame(() => {
            const el = document.querySelector<HTMLElement>('[data-field="categoryName"]');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.querySelector<HTMLElement>('input')?.focus();
            }
          });
          return;
        }
      } catch {
        // If validation endpoint fails, proceed with save
      }
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode]);

  // --- Confirmed save (3-stage: confirm → loading → toast) ---
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);

    try {
      const payload: CreateRoomCategoryRequest | UpdateRoomCategoryRequest = {
        propertyId: formData.propertyId,
        categoryCode: formData.categoryCode.trim(),
        categoryName: formData.categoryName.trim(),
        shortDescription: formData.shortDescription || undefined,
        longDescription: formData.longDescription || undefined,
        baseOccupancy: parseInt(formData.baseOccupancy, 10) || 2,
        maxOccupancy: parseInt(formData.maxOccupancy, 10) || 3,
        maxAdults: parseInt(formData.maxAdults, 10) || 2,
        maxChildren: parseInt(formData.maxChildren, 10) || 1,
        bedType: formData.bedType || undefined,
        bedCount: parseInt(formData.bedCount, 10) || 1,
        roomSizeSqft: formData.roomSizeSqft ? parseFloat(formData.roomSizeSqft) : undefined,
        roomView: formData.roomView || undefined,
        smokingAllowed: formData.smokingAllowed,
        totalInventory: parseInt(formData.totalInventory, 10) || 0,
        displayOrder: parseInt(formData.displayOrder, 10) || 1,
        isActive: formData.isActive,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await roomCategoryService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdateRoomCategoryRequest);
      } else {
        // create and copy both call create
        await roomCategoryService.create(payload as CreateRoomCategoryRequest);
      }

      setIsSaving(false);
      notificationService.toast.success(
        `"${formData.categoryName}" ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave]);

  // --- Refresh from server after concurrency conflict ---
  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff?.id) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await roomCategoryService.getById(eff.id);
      const fresh = response.data;
      effectiveRecordRef.current = fresh;
      const data: RoomCategoryFormData = {
        propertyId: fresh.propertyId || '',
        categoryCode: fresh.categoryCode || '',
        categoryName: fresh.categoryName || '',
        shortDescription: fresh.shortDescription || '',
        longDescription: fresh.longDescription || '',
        baseOccupancy: fresh.baseOccupancy != null ? String(fresh.baseOccupancy) : '2',
        maxOccupancy: fresh.maxOccupancy != null ? String(fresh.maxOccupancy) : '3',
        maxAdults: fresh.maxAdults != null ? String(fresh.maxAdults) : '2',
        maxChildren: fresh.maxChildren != null ? String(fresh.maxChildren) : '1',
        bedType: fresh.bedType || 'King',
        bedCount: fresh.bedCount != null ? String(fresh.bedCount) : '1',
        roomSizeSqft: fresh.roomSizeSqft != null ? String(fresh.roomSizeSqft) : '',
        roomView: fresh.roomView || '',
        smokingAllowed: fresh.smokingAllowed,
        totalInventory: fresh.totalInventory != null ? String(fresh.totalInventory) : '',
        displayOrder: fresh.displayOrder != null ? String(fresh.displayOrder) : '1',
        isActive: fresh.isActive,
      };
      setFormData(data);
      initialSnapshotRef.current = JSON.stringify(data);
      setFieldErrors({});
      setConcurrencyConflict({ open: false, message: '' });
      notificationService.toast.success('Latest version loaded.');
    } catch (refreshErr: any) {
      const msg = refreshErr?.response?.data?.message || refreshErr?.message || 'Failed to load latest version.';
      notificationService.toast.error(msg);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // --- Code validation on blur ---
  const handleCodeBlur = useCallback(async () => {
    const code = formData.categoryCode.trim();
    if (!code) return;
    try {
      const excludeId = mode === 'edit' && record ? record.id : undefined;
      const response = await roomCategoryService.validateCode(code, formData.propertyId, excludeId);
      if (!response.data) {
        setFieldErrors(prev => ({ ...prev, categoryCode: 'This category code already exists' }));
      }
    } catch {
      // Silently ignore validation errors
    }
  }, [formData.categoryCode, formData.propertyId, mode, record]);

  // --- Name validation on blur ---
  const handleNameBlur = useCallback(async () => {
    const name = formData.categoryName.trim();
    if (!name) return;
    try {
      const excludeId = mode === 'edit' && record ? record.id : undefined;
      const response = await roomCategoryService.validateName(name, formData.propertyId, excludeId);
      if (!response.data) {
        setFieldErrors(prev => ({ ...prev, categoryName: 'This category name already exists' }));
      }
    } catch {
      // Silently ignore validation errors
    }
  }, [formData.categoryName, formData.propertyId, mode, record]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        {/* Loading overlay */}
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem'
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite'
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'edit' ? 'Saving...' : 'Creating...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Room Category' : mode === 'copy' ? 'Copy Room Category' : 'Edit Room Category'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Property --- */}
          <div className="form-group" data-field="propertyId" style={{ marginBottom: '1rem' }}>
            <SyutSelect
              label={<>Property <span className="text-danger">*</span></>}
              isAsync
              loadOptions={loadPropertyOptions}
              defaultOptions={true}
              cacheOptions={false}
              seedOptions={initialPropertyOption ? [initialPropertyOption] : undefined}
              value={formData.propertyId}
              onChange={(val) => { clearFieldError('propertyId'); setFormData(prev => ({ ...prev, propertyId: (val as string) || '' })); }}
              placeholder="Select property..."
              isClearable={false}
              size="md"
              menuPortalTarget={document.body}
              error={getFieldError('propertyId')}
            />
          </div>

          {/* --- Basic Information --- */}
          <div className="form-grid-2">
            <div className="form-group" data-field="categoryCode">
              <label className="form-label">Category Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('categoryCode') ? ' form-field-error' : ''}`}
                value={formData.categoryCode}
                onChange={(e) => { clearFieldError('categoryCode'); setFormData(prev => ({ ...prev, categoryCode: e.target.value })); }}
                onBlur={handleCodeBlur}
                placeholder="e.g., DLX"
                maxLength={20}
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : undefined}
                autoFocus={mode === 'create' || mode === 'copy'}
              />
              {getFieldError('categoryCode') && <div className="form-error-message">{getFieldError('categoryCode')}</div>}
            </div>

            <div className="form-group" data-field="categoryName">
              <label className="form-label">Category Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('categoryName') ? ' form-field-error' : ''}`}
                value={formData.categoryName}
                onChange={(e) => { clearFieldError('categoryName'); setFormData(prev => ({ ...prev, categoryName: e.target.value })); }}
                onBlur={handleNameBlur}
                placeholder="e.g., Deluxe Room"
                maxLength={100}
              />
              {getFieldError('categoryName') && <div className="form-error-message">{getFieldError('categoryName')}</div>}
            </div>
          </div>

          {/* --- Descriptions --- */}
          <div className="form-group" data-field="shortDescription">
            <label className="form-label">Short Description</label>
            <input
              type="text"
              className={`form-input${getFieldError('shortDescription') ? ' form-field-error' : ''}`}
              value={formData.shortDescription}
              onChange={(e) => { clearFieldError('shortDescription'); setFormData(prev => ({ ...prev, shortDescription: e.target.value })); }}
              placeholder="Brief description..."
              maxLength={200}
            />
            {getFieldError('shortDescription') && <div className="form-error-message">{getFieldError('shortDescription')}</div>}
          </div>

          <div className="form-group" data-field="longDescription">
            <label className="form-label">Long Description</label>
            <textarea
              className={`form-input${getFieldError('longDescription') ? ' form-field-error' : ''}`}
              value={formData.longDescription}
              onChange={(e) => { clearFieldError('longDescription'); setFormData(prev => ({ ...prev, longDescription: e.target.value })); }}
              placeholder="Detailed description..."
              maxLength={1000}
              rows={3}
              style={{ resize: 'vertical' }}
            />
            {getFieldError('longDescription') && <div className="form-error-message">{getFieldError('longDescription')}</div>}
          </div>

          {/* --- Occupancy --- */}
          <div className="form-grid-4">
            <div className="form-group" data-field="baseOccupancy">
              <label className="form-label">Base Occupancy <span className="text-danger">*</span></label>
              <input
                type="number"
                className={`form-input${getFieldError('baseOccupancy') ? ' form-field-error' : ''}`}
                value={formData.baseOccupancy}
                onChange={(e) => { clearFieldError('baseOccupancy'); setFormData(prev => ({ ...prev, baseOccupancy: e.target.value })); }}
                min="1"
              />
              {getFieldError('baseOccupancy') && <div className="form-error-message">{getFieldError('baseOccupancy')}</div>}
            </div>

            <div className="form-group" data-field="maxOccupancy">
              <label className="form-label">Max Occupancy <span className="text-danger">*</span></label>
              <input
                type="number"
                className={`form-input${getFieldError('maxOccupancy') ? ' form-field-error' : ''}`}
                value={formData.maxOccupancy}
                onChange={(e) => { clearFieldError('maxOccupancy'); setFormData(prev => ({ ...prev, maxOccupancy: e.target.value })); }}
                min="1"
              />
              {getFieldError('maxOccupancy') && <div className="form-error-message">{getFieldError('maxOccupancy')}</div>}
            </div>

            <div className="form-group" data-field="maxAdults">
              <label className="form-label">Max Adults</label>
              <input
                type="number"
                className={`form-input${getFieldError('maxAdults') ? ' form-field-error' : ''}`}
                value={formData.maxAdults}
                onChange={(e) => {
                  clearFieldError('maxAdults');
                  clearFieldError('maxOccupancy');
                  setFormData(prev => ({ ...prev, maxAdults: e.target.value }));
                }}
                min="0"
              />
              {getFieldError('maxAdults') && <div className="form-error-message">{getFieldError('maxAdults')}</div>}
            </div>

            <div className="form-group" data-field="maxChildren">
              <label className="form-label">Max Children</label>
              <input
                type="number"
                className={`form-input${getFieldError('maxChildren') ? ' form-field-error' : ''}`}
                value={formData.maxChildren}
                onChange={(e) => {
                  clearFieldError('maxChildren');
                  clearFieldError('maxOccupancy');
                  setFormData(prev => ({ ...prev, maxChildren: e.target.value }));
                }}
                min="0"
              />
              {getFieldError('maxChildren') && <div className="form-error-message">{getFieldError('maxChildren')}</div>}
            </div>
          </div>

          {/* --- Bed & Room --- */}
          <div className="form-grid-4">
            <div className="form-group" data-field="bedType">
              <SyutSelect
                label="Bed Type"
                options={bedTypeOptions}
                value={formData.bedType}
                onChange={(val) => setFormData(prev => ({ ...prev, bedType: (val as string) || '' }))}
                placeholder="Select..."
                isClearable
                size="md"
                menuPortalTarget={document.body}
              />
            </div>

            <div className="form-group" data-field="bedCount">
              <label className="form-label">Bed Count</label>
              <input
                type="number"
                className="form-input"
                value={formData.bedCount}
                onChange={(e) => setFormData(prev => ({ ...prev, bedCount: e.target.value }))}
                min="0"
              />
            </div>

            <div className="form-group" data-field="roomSizeSqft">
              <label className="form-label">Room Size (sqft)</label>
              <input
                type="number"
                className="form-input"
                value={formData.roomSizeSqft}
                onChange={(e) => setFormData(prev => ({ ...prev, roomSizeSqft: e.target.value }))}
                placeholder="e.g., 450"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group" data-field="roomView">
              <SyutSelect
                label="Room View"
                options={roomViewOptions}
                value={formData.roomView}
                onChange={(val) => setFormData(prev => ({ ...prev, roomView: (val as string) || '' }))}
                placeholder="Select..."
                isClearable
                size="md"
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          {/* --- Inventory & Settings --- */}
          <div className="form-grid-4">
            <div className="form-group" data-field="totalInventory">
              <label className="form-label">Total Inventory <span className="text-danger">*</span></label>
              <input
                type="number"
                className={`form-input${getFieldError('totalInventory') ? ' form-field-error' : ''}`}
                value={formData.totalInventory}
                onChange={(e) => { clearFieldError('totalInventory'); setFormData(prev => ({ ...prev, totalInventory: e.target.value })); }}
                placeholder="e.g., 20"
                min="0"
              />
              {getFieldError('totalInventory') && <div className="form-error-message">{getFieldError('totalInventory')}</div>}
            </div>

            <div className="form-group" data-field="displayOrder">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                className="form-input"
                value={formData.displayOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: e.target.value }))}
                min="0"
              />
            </div>

            <div className="form-group" data-field="smokingAllowed">
              <SyutSelect
                label="Smoking Allowed"
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
                value={String(formData.smokingAllowed)}
                onChange={(val) => setFormData(prev => ({ ...prev, smokingAllowed: val === 'true' }))}
                isClearable={false}
                size="md"
                menuPortalTarget={document.body}
              />
            </div>

            <div className="form-group" data-field="isActive">
              <SyutSelect
                label="Status"
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
                value={String(formData.isActive)}
                onChange={(val) => setFormData(prev => ({ ...prev, isActive: val === 'true' }))}
                isClearable={false}
                size="md"
                menuPortalTarget={document.body}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
          >
            <IconSystem.save size={16} />
            {mode === 'edit' ? 'Save Changes' : 'Create Room Category'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} the room category "${formData.categoryName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* Concurrency conflict — refresh from server */}
      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default RoomCategoryModal;
